import {
  Button,
  color,
  InputGroup,
  InputSelect,
  NO_REGEX,
  SWIFT_CODE_REGEX,
} from "@funded-here-interface/common";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setBankNameRedux,
  setAccountHolderNameRedux,
  setAccountNoRedux,
  setSwiftCodeReduct,
  setAccountTypeRedux,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
} from "../../features/merchant/bankSlice";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { bankAccountTypes } from "../../constants/bank";
import BankAccNameDisclaimer from "../../components/BankAccNameDisclaimer/BankAccNameDisclaimer";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { useStyle } from "../../styles/BankDetailsCss";
import {
  countries,
  AccountCurrencyTypeEnum,
} from "@funded-here-interface/common/src/constant/country";
import { useBorrowerBankDraftSync } from "./hooks/useBorrowerBankDraftSync";

const currencyOptions = Object.values(AccountCurrencyTypeEnum);

interface BorrowerBankProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onSaveDraft?: (callback: () => void) => void;
  onResetForm?: (callback: () => void) => void;
  errors?: { [key: string]: string };
  onClearError?: (field: string) => void;
}

const BorrowerBank: FC<BorrowerBankProps> = ({
  onDirtyChange,
  onSaveDraft,
  onResetForm,
  errors,
  onClearError,
}) => {
  const { classes } = useStyle();
  const dispatch = useDispatch();

  // Redux selectors
  const {
    bankName: bankNameRedux,
    accountHolderName: accountHolderNameRedux,
    accountNumber: accountNoRedux,
    accountType: accountTypeRedux,
    swiftCode: swiftCodeRedux,
    bankAddress: bankAddressRedux,
    bankCountry: bankCountryRedux,
    currency: currencyRedux,
  } = useSelector((state: RootState) => state.BorBank);
  const comp = useSelector((state: RootState) => state.borBiz.company);
  const { token, userId } = useSelector((state: RootState) => state.auth);

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Custom hooks
  const { syncBorrowerBankToRedux } = useBorrowerBankDraftSync();

  // Page and dirty state
  const [initPage, setInitPage] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [shouldMarkDirty, setShouldMarkDirty] = useState(true);
  const [initialDraftData, setInitialDraftData] = useState<any>(null);

  // Local state
  const [bankName, setBankName] = useState(bankNameRedux || "");
  const [accountNumber, setAccountNumber] = useState(accountNoRedux || "");
  const [accountType, setAccountType] = useState(accountTypeRedux || "");
  const [swiftCode, setSwiftCode] = useState(swiftCodeRedux || "");
  const [accountHolderName, setAccountHolderName] = useState(
    accountHolderNameRedux || ""
  );
  const [bankAddress, setBankAddress] = useState(bankAddressRedux || "");
  const [bankCountry, setBankCountry] = useState(bankCountryRedux || "");
  const [currency, setCurrency] = useState(currencyRedux || "");
  const [accountHolderNameError, setAccountHolderNameError] = useState("");

  /**
   * Generic input change handler with dirty tracking and error clearing
   */
  const handleInputChange = (
    setter: (value: string) => void,
    value: string,
    errorField?: string
  ) => {
    setter(value);
    if (shouldMarkDirty) {
      setIsDirty(true);
      onDirtyChange?.(true);
    }
    if (errorField) {
      onClearError?.(errorField);
    }
  };

  /**
   * Handle numeric input (remove non-numeric characters)
   */
  const handleNumericInputChange =
    (setter: (value: string) => void, errorField?: string) =>
    (value: string) => {
      const numericValue = value.replace(NO_REGEX, "");
      handleInputChange(setter, numericValue, errorField);
    };

  /**
   * Handle SWIFT code input (clean invalid characters)
   */
  const handleSwiftCodeChange = (value: string) => {
    const cleanedValue = value.replace(SWIFT_CODE_REGEX, "");
    handleInputChange(setSwiftCode, cleanedValue, "swiftCode");
  };

  /**
   * Validate account holder name matches company name
   * Returns boolean only - error display handled separately
   */
  const validateAccountHolderName = (): boolean => {
    const hasError = accountHolderName && comp && accountHolderName !== comp;
    return !hasError;
  };

  /**
   * Save draft to backend
   */
  const saveDraft = (showToast: boolean = true) => {
    const isAccountHolderNameValid = validateAccountHolderName();

    // Show error message below input and toast if account holder name is invalid
    if (!isAccountHolderNameValid) {
      setAccountHolderNameError(
        "Account holder name must match the company name"
      );
      toast.error("Account holder name must match the company name", {
        autoClose: 2000,
      });
    } else {
      setAccountHolderNameError("");
    }

    // Prepare bank data - only include accountHolderName if valid
    const bankData = {
      bankName,
      accountNumber,
      accountType,
      swiftCode,
      accountHolderName: isAccountHolderNameValid ? accountHolderName : "",
      bankAddress,
      bankCountry,
      currency,
    };

    // Check if there are other fields to save (besides accountHolderName)
    const hasOtherFieldsToSave =
      bankName ||
      accountNumber ||
      accountType ||
      swiftCode ||
      bankAddress ||
      bankCountry ||
      currency;

    // If only accountHolderName is filled and it's invalid, don't save
    if (!isAccountHolderNameValid && !hasOtherFieldsToSave) {
      return;
    }

    // Sync to Redux (only update account holder name if valid)
    dispatch(setBankNameRedux(bankName));
    dispatch(setAccountNoRedux(accountNumber));
    dispatch(setAccountTypeRedux(accountType));
    dispatch(setSwiftCodeReduct(swiftCode));
    dispatch(setBankAddressRedux(bankAddress));
    dispatch(setBankCountryRedux(bankCountry));
    dispatch(setCurrencyRedux(currency));

    if (isAccountHolderNameValid) {
      dispatch(setAccountHolderNameRedux(accountHolderName));
    }

    // Prepare save payload
    const draftData: any = {
      userId: parseInt(userId),
      token,
      bankName,
      accountNumber,
      accountType,
      swiftCode,
      bankAddress,
      bankCountry,
      currency,
    };

    // Only include accountHolderName if valid
    if (isAccountHolderNameValid) {
      draftData.accountHolderName = accountHolderName;
    }

    saveOrgDraftMutation.mutateAsync(draftData, {
      onSuccess: () => {
        setIsDirty(false);
        onDirtyChange?.(false);
        setInitialDraftData(bankData);
        if (showToast && hasOtherFieldsToSave) {
          toast.success("Draft Saved", { autoClose: 2000 });
        }
      },
      onError: (e) => {
        toast.error((e as Error).message);
      },
    });
  };

  /**
   * Reset form to initial draft data
   */
  const resetForm = () => {
    if (initialDraftData) {
      const bankData = {
        bankName: initialDraftData.bankName || "",
        accountNumber: initialDraftData.accountNumber || "",
        accountType: initialDraftData.accountType || "",
        swiftCode: initialDraftData.swiftCode || "",
        accountHolderName: initialDraftData.accountHolderName || "",
        bankAddress: initialDraftData.bankAddress || "",
        bankCountry: initialDraftData.bankCountry || "",
        currency: initialDraftData.currency || "",
      };

      // Set local states
      setBankName(bankData.bankName);
      setAccountNumber(bankData.accountNumber);
      setAccountType(bankData.accountType);
      setSwiftCode(bankData.swiftCode);
      setAccountHolderName(bankData.accountHolderName);
      setBankAddress(bankData.bankAddress);
      setBankCountry(bankData.bankCountry);
      setCurrency(bankData.currency);

      // Sync to Redux
      syncBorrowerBankToRedux(bankData);

      setAccountHolderNameError("");
      setIsDirty(false);
      onDirtyChange?.(false);
    }
  };

  /**
   * Load draft data from backend and sync to Redux
   */
  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          setShouldMarkDirty(false);
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);

          if (data) {
            const bankData = {
              bankName: data.bankName || "",
              accountNumber: data.accountNumber || "",
              accountType: data.accountType || "",
              swiftCode: data.swiftCode || "",
              accountHolderName: data.accountHolderName || "",
              bankAddress: data.bankAddress || "",
              bankCountry: data.bankCountry || "",
              currency: data.currency || "",
            };

            setInitialDraftData(bankData);

            // Set local states
            setBankName(bankData.bankName);
            setAccountNumber(bankData.accountNumber);
            setAccountType(bankData.accountType);
            setSwiftCode(bankData.swiftCode);
            setAccountHolderName(bankData.accountHolderName);
            setBankAddress(bankData.bankAddress);
            setBankCountry(bankData.bankCountry);
            setCurrency(bankData.currency);

            // Sync to Redux
            syncBorrowerBankToRedux(bankData);

            setIsDirty(false);
            onDirtyChange?.(false);
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
          setShouldMarkDirty(true);
        }
      }
    };
    initializeDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPage, userId, token]);

  /**
   * Register save draft and reset form callbacks
   */
  useEffect(() => {
    if (onSaveDraft) {
      onSaveDraft(saveDraft);
    }
    if (onResetForm) {
      onResetForm(resetForm);
    }
  }, [onSaveDraft, onResetForm]);

  return (
    <div style={{ marginTop: "20px" }} className={classes.stepContent}>
      <BankAccNameDisclaimer />
      <InputGroup
        isErrored={errors?.accountHolderName || accountHolderNameError}
        errorMessage={!!(errors?.accountHolderName || accountHolderNameError)}
        onChange={(value: string) => {
          handleInputChange(setAccountHolderName, value, "accountHolderName");
          if (accountHolderNameError) {
            setAccountHolderNameError("");
          }
        }}
        labelText="Account Holder Name"
        id="account_holder_name"
        value={accountHolderName}
      />
      <InputGroup
        isErrored={errors?.bankName}
        errorMessage={!!errors?.bankName}
        onChange={(value: string) =>
          handleInputChange(setBankName, value, "bankName")
        }
        labelText="Bank Name"
        id="bank_name"
        value={bankName}
      />
      <InputGroup
        isErrored={errors?.accountNumber}
        errorMessage={!!errors?.accountNumber}
        onChange={handleNumericInputChange(setAccountNumber, "accountNumber")}
        labelText="Bank Account Number"
        id="account_number"
        value={accountNumber}
      />
      <InputSelect
        isErrored={errors?.accountType}
        errorMessage={!!errors?.accountType}
        onChange={(value: string) =>
          handleInputChange(setAccountType, value, "accountType")
        }
        labelText="Bank Account Type"
        id="accountType"
        value={accountType}
        data={bankAccountTypes}
      />
      <InputGroup
        isErrored={errors?.swiftCode}
        errorMessage={!!errors?.swiftCode}
        onChange={handleSwiftCodeChange}
        labelText="Bank SWIFT Code"
        id="swift_code"
        value={swiftCode}
      />
      <InputGroup
        isErrored={errors?.bankAddress}
        errorMessage={!!errors?.bankAddress}
        onChange={(value: string) =>
          handleInputChange(setBankAddress, value, "bankAddress")
        }
        labelText="Bank Address"
        id="bank_address"
        value={bankAddress}
      />
      <InputSelect
        isErrored={errors?.bankCountry}
        errorMessage={!!errors?.bankCountry}
        onChange={(value: string) =>
          handleInputChange(setBankCountry, value, "bankCountry")
        }
        labelText="Bank Country"
        id="bank_country"
        value={bankCountry}
        data={countries}
      />
      <InputSelect
        isErrored={errors?.currency}
        errorMessage={!!errors?.currency}
        onChange={(value: string) =>
          handleInputChange(setCurrency, value, "currency")
        }
        labelText="Currency"
        id="currency"
        value={currency}
        data={currencyOptions}
      />
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft(true)}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
        />
      </div>
    </div>
  );
};

export default BorrowerBank;
